import { lazy } from "react";
// router
import { createBrowserRouter, Navigate } from "react-router-dom";
// layouts
import MainLayout from "../components/layout/MainLayout";
import ProtectedRoutes from "../components/layout/ProtectedRoutes";

// pages
import Home from "../pages/home/Home";
import NotFound from "../pages/not-found/NotFound";
import AllowedTo from "../components/layout/AllowedTo";
// lazy pages
const Login = lazy(() => import("../pages/login/Login"));
// orders
const Orders = lazy(() => import("../pages/orders/Orders"));
const OrderDetails = lazy(() => import("../pages/orders/OrderDetails"));
const OrderStatistics = lazy(() => import("../pages/orders/orders-statistics"));
const ActiveOrders = lazy(() => import("../pages/orders/ActiveOrders"));
// items
const Items = lazy(() => import("../pages/items/Items"));
const AddItem = lazy(() => import("../pages/items/AddItem"));
const EditItem = lazy(() => import("../pages/items/EditItem"));
// categories
const Categories = lazy(() => import("../pages/categories/Categories"));
const AddCategory = lazy(() => import("../pages/categories/AddCategory"));
const EditCategory = lazy(() => import("../pages/categories/EditCategory"));
// payment methods
const PaymentMethods = lazy(() => import("../pages/categories/PaymentMethods"));
// cities
const Cities = lazy(() => import("../pages/cities/Cities"));
const AddCity = lazy(() => import("../pages/cities/AddCity"));
const EditCity = lazy(() => import("../pages/cities/EditCity"));
// areas
const Areas = lazy(() => import("../pages/areas/Areas"));
const AddArea = lazy(() => import("../pages/areas/AddArea"));
const EditArea = lazy(() => import("../pages/areas/EditArea"));
// users
const Users = lazy(() => import("../pages/users/Users"));
const AddUser = lazy(() => import("../pages/users/AddUser"));
const EditUser = lazy(() => import("../pages/users/EditUser"));
// linked accounts
const LinkedAccounts = lazy(() => import("../pages/linked-accounts/LinkedAccounts"));
const LinkedAccountDetails = lazy(() => import("../pages/linked-accounts/LinkedAccountDetails"));
// transactions
const Transactions = lazy(() => import("../pages/transactions/Transactions"));
// notifications
const PushNotification = lazy(
  () => import("../pages/notifications/PushNotification"),
);
// banners
const Banners = lazy(() => import("../pages/banners/Banners"));
const AddBanner = lazy(() => import("../pages/banners/AddBanner"));
const EditBanner = lazy(() => import("../pages/banners/EditBanner"));
// settings
const Settings = lazy(() => import("../pages/settings/Settings"));
// invoices
const Invoices = lazy(() => import("../pages/invoices/Invoices"));
// businesses
const Businesses = lazy(() => import("../pages/businesses/Businesses"));
const AddBusiness = lazy(() => import("../pages/businesses/AddBusiness"));
const EditBusiness = lazy(() => import("../pages/businesses/EditBusiness"));
// units
const Units = lazy(() => import("../pages/units/Units"));
const AddUnit = lazy(() => import("../pages/units/AddUnit"));
const EditUnit = lazy(() => import("../pages/units/EditUnit"));
// settlements
const Settlements = lazy(() => import("../pages/settlements/Settlements"));
const ShowSettlements = lazy(
  () => import("../pages/settlements/ShowSettlements"),
);
const ScheduledOrders = lazy(
  () => import("../pages/scheduled-orders/ScheduledOrders"),
);
// pricelists
const Pricelists = lazy(() => import("../pages/pricelists/Pricelists"));
const AddPricelist = lazy(() => import("../pages/pricelists/AddPricelist"));
const EditPricelist = lazy(() => import("../pages/pricelists/EditPricelist"));
// receipt vouchers
const ReceiptDetails = lazy(() => import("../pages/receipts/ReceiptDetails"));
// admin settlements
const AdminSettlements = lazy(() => import("../pages/admin-settlements/AdminSettlements"));
const AdminSettlementDetails = lazy(() => import("../pages/admin-settlements/AdminSettlementDetails"));
const AccountManagerSettlements = lazy(() => import("../pages/admin-settlements/AccountManagerSettlements"));
const ActivityLogs = lazy(() => import("../pages/activity-logs/ActivityLogs"));
// all routes

const router = createBrowserRouter([
  {
    path: "/",
    element: (
      <ProtectedRoutes>
        <MainLayout />
      </ProtectedRoutes>
    ),
    children: [
      {
        path: "",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <Home />,
          },
        ],
      },
      // activity logs
      {
        path: "activity-logs",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <ActivityLogs />,
          },
        ],
      },
      // order
      {
        path: "orders",
        element: <AllowedTo allowedTo={["admin", "accountant"]} />,
        children: [
          {
            index: true,
            element: <Orders />,
          },
          {
            path: ":orderId/show",
            element: <OrderDetails />,
          },
          {
            path: "statistics",
            element: <OrderStatistics />,
          },
          {
            path: "active-orders",
            element: <ActiveOrders />,
          },
        ],
      },
      // scheduled orders
      {
        path: "scheduled-orders",
        element: <AllowedTo allowedTo={["admin", "accountant"]} />,
        children: [{ index: true, element: <ScheduledOrders /> }],
      },
      // items
      {
        path: "items",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <Items />,
          },
          {
            path: "add",
            element: <AddItem />,
          },
          {
            path: ":itemId",
            element: <EditItem />,
          },
        ],
      },
      // categories
      {
        path: "categories",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <Categories />,
          },
          {
            path: "add",
            element: <AddCategory />,
          },
          {
            path: ":categoryId",
            element: <EditCategory />,
          },
        ],
      },
      // payment-methods
      {
        path: "payment-methods",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <PaymentMethods />,
          },
          {
            path: "add",
            element: <AddCategory />,
          },
          {
            path: ":categoryId",
            element: <EditCategory />,
          },
        ],
      },
      // cities
      {
        path: "cities",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <Cities />,
          },
          {
            path: "add",
            element: <AddCity />,
          },
          {
            path: ":cityId",
            element: <EditCity />,
          },
        ],
      },
      // business
      {
        path: "businesses",
        element: <AllowedTo allowedTo={["superadmin", "admin"]} />,
        children: [
          {
            index: true,
            element: <Businesses />,
          },
          {
            path: "add",
            element: <AddBusiness />,
          },
          {
            path: ":businessId",
            element: <EditBusiness />,
          },
        ],
      },
      // units
      {
        path: "units",
        element: <AllowedTo allowedTo={["superadmin", "admin"]} />,
        children: [
          {
            index: true,
            element: <Units />,
          },
          {
            path: "add",
            element: <AddUnit />,
          },
          {
            path: ":unitId",
            element: <EditUnit />,
          },
        ],
      },
      // areas
      {
        path: "areas",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <Areas />,
          },
          {
            path: "add",
            element: <AddArea />,
          },
          {
            path: ":areaId",
            element: <EditArea />,
          },
        ],
      },
      // users
      {
        path: "users",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <Users />,
          },
          {
            path: "add",
            element: <AddUser />,
          },
          {
            path: ":userId",
            element: <EditUser />,
          },
        ],
      },
      // linked-accounts
      {
        path: "linked-accounts",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <LinkedAccounts />,
          },
          {
            path: ":id",
            element: <LinkedAccountDetails />,
          },
        ],
      },
      // transactions
      {
        path: "transactions",
        element: <AllowedTo allowedTo={["admin", "accountant"]} />,
        children: [
          {
            index: true,
            element: <Transactions />,
          },
        ],
      },
      // notifications
      {
        path: "push-notifications",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <PushNotification />,
          },
        ],
      },
      // banners
      {
        path: "banners",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <Banners />,
          },
          {
            path: "add",
            element: <AddBanner />,
          },
          {
            path: ":bannerId",
            element: <EditBanner />,
          },
        ],
      },
      // invoices
      {
        path: "invoices",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <Invoices />,
          },
          {
            path: "add",
            element: <AddBanner />,
          },
          {
            path: ":bannerId",
            element: <EditBanner />,
          },
        ],
      },
      // settings
      // {
      //   path: "settlements",
      //   element: <AllowedTo allowedTo={["admin", "accountant"]} />,
      //   children: [
      //     {
      //       index: true,
      //       element: <Settlements />,
      //     },
      //     {
      //       path: ":settlementId/show",
      //       element: <ShowSettlements />,
      //     },
      //   ],
      // },
      // pricelists
      {
        path: "pricelists",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <Pricelists />,
          },
          {
            path: "add",
            element: <AddPricelist />,
          },
          {
            path: ":pricelistId",
            element: <EditPricelist />,
          },
        ],
      },
      // receipts — the index route now redirects to the unified
      // account-manager ledger; :id/view stays as the deep-link target
      // for a single receipt, reached from within a manager's panel.
      {
        path: "receipts",
        element: <AllowedTo allowedTo={["admin", "accountant"]} />,
        children: [
          { index: true, element: <Navigate to="/admin-settlements" replace /> },
          {
            path: ":id/view",
            element: <ReceiptDetails />,
          },
        ],
      },
      // admin settlements
      {
        path: "admin-settlements",
        element: <AllowedTo allowedTo={["admin", "accountant", "account-manager"]} />,
        children: [
          { index: true, element: <AdminSettlements /> },
          {
            path: ":id/view",
            element: <AdminSettlementDetails />,
          },
          {
            path: "managers/:id",
            element: <AccountManagerSettlements />,
          },
        ],
      },
      // settings
      {
        path: "settings",
        element: <AllowedTo allowedTo={["admin"]} />,
        children: [
          {
            index: true,
            element: <Settings />,
          },
        ],
      },
    ],
  },
  {
    path: "/login",
    element: <Login />,
  },
  {
    path: "*",
    element: <NotFound />,
  },
]);
export default router;
